import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { visionURL } from "../config/environment";
import { isIframed } from "../utils/iframe";
import { fetchUserById } from "../redux/actions/userActions";
import { selectUserById } from "../redux/reducers/userReducer";

let loaderScript;

const apiUrl = `${visionURL}/flipflop_features`;

const useFlipFlopFeatures = () => {
  const [data, setData] = useState({});
  useEffect(() => {
    fetch(apiUrl, {
      method: "GET",
      credentials: "include"
    })
      .then(response => {
        const unauthorized = response.status === 401;

        if (isIframed && unauthorized) {
          window.parent.postMessage("sessionTimeout", "*");
        }
        return response.json();
      })
      .then(setData)
      .catch(error => {
        console.error("Fetch error:", error);
      });
  }, []);

  return data;
};

export const usePendoEnabled = () => {
  const { data } = useFlipFlopFeatures();
  return useMemo(
    () =>
      data?.find(({ attributes }) => attributes.key === "pendo_integration")
        ?.attributes.enabled,
    [data]
  );
};

const createAccount = customer => {
  const {
    id: customerId,
    name: customerName,
    packageName: customerTier,
    forceId,
    included
  } = customer;

  const customerFeatureGroups = included
    ?.filter(({ attributes }) => attributes?.sendToPendo)
    .map(({ attributes }) => attributes.name);

  return {
    id: customerId,
    name: customerName,
    customer_tier: customerTier,
    salesforce_id: forceId,
    feature_groups: customerFeatureGroups
  };
};

const createVisitor = ({
  id: userId,
  firstName,
  lastName,
  included,
  email
}) => {
  const roles = included
    ?.filter(
      ({ type, attributes }) => type === "role" && attributes.assignableInPhq
    )
    .map(({ attributes }) => attributes.name);
  const position = included
    ?.filter(({ type }) => type === "position")
    .map(({ attributes }) => attributes.name)
    .join(", ");

  return {
    id: userId,
    full_name: `${firstName} ${lastName}`,
    user_firstName: firstName,
    user_lastName: lastName,
    position,
    roles,
    email: email?.toLowerCase()
  };
};

export const usePendo = () => {
  const dispatch = useDispatch();
  const [needsInit, setNeedsInit] = useState(false);
  const pendo = window.pendo;
  const initialized = pendo?.isReady?.();

  const enabled = usePendoEnabled();

  useEffect(() => {
    if (enabled) {
      dispatch(fetchUserById("true", { include: "roles,position" }));
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || loaderScript?.isConnected) {
      return;
    }
    loaderScript = document.createElement("script");
    loaderScript.innerHTML = `(function(apiKey){ (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[]; v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){ o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]); y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js'; z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');})('0490c1dd-ebea-4fd9-7a90-eb60535af04a'); `;
    document.body.appendChild(loaderScript);
    setNeedsInit(true);
    return () => loaderScript.remove();
  }, [enabled]);

  const { data: user, loading: isUserLoading } = useSelector(state =>
    selectUserById(state, "true")
  );
  const { customer, loading: isCustomerLoading } = useSelector(
    ({ customerState }) => customerState
  );

  useEffect(() => {
    if (
      !initialized &&
      needsInit &&
      user &&
      customer &&
      !isUserLoading &&
      !isCustomerLoading
    ) {
      const instance = {
        visitor: createVisitor(user),
        account: createAccount(customer)
      };
      pendo.initialize(instance);

      setNeedsInit(false);
    }
  }, [initialized, customer, user, isUserLoading, isCustomerLoading, pendo]);

  useEffect(() => {
    if (
      initialized &&
      customer &&
      user &&
      !isUserLoading &&
      !isCustomerLoading
    ) {
      const instance = {
        visitor: createVisitor(user),
        account: createAccount(customer)
      };
      pendo.updateOptions(instance);
    }
  }, [initialized, customer, user, isUserLoading, isCustomerLoading, pendo]);
};
